import React, { useMemo } from "react"
import ReactApexChart from "react-apexcharts"
import moment from "moment"
import { getWeekTooltipLabel } from "utils/dateUtil"

const MarginLineChart = ({
  data = [],
  title = "Margin",
  color = "#4361ee",
  gradientColor = "#4361ee",
  icon = "📈",
  valueKey,
  isPercent = false,
}) => {
  const sorted = useMemo(
    () => [...data].sort((a, b) => new Date(a.date) - new Date(b.date)),
    [data]
  )

  const useWeeklyLabels = true
  // ✅ Always store actual dates for tooltip
  const actualDates = sorted.map(i => moment(i.date).format("DD-MM-YYYY"))

  // ✅ X-axis: W-1, W-2... if > 10 points, else actual dates
  const categories = useWeeklyLabels
    ? sorted.map((_, i) => `${i + 1}`)
    : actualDates

  const values = sorted.map(i => Number(i[valueKey]) || 0)

  const total = values.reduce((a, b) => a + b, 0)
  const avg = values.length ? total / values.length : 0
  const max = values.length ? Math.max(...values) : 0
  const min = values.length ? Math.min(...values) : 0

  const half = Math.floor(values.length / 2)
  const firstHalf = values.slice(0, half)
  const secHalf = values.slice(half)
  const firstAvg =
    firstHalf.reduce((a, b) => a + b, 0) / (firstHalf.length || 1)
  const secAvg = secHalf.reduce((a, b) => a + b, 0) / (secHalf.length || 1)
  const trendUp = secAvg >= firstAvg
  const trendPct =
    firstAvg > 0 ? (((secAvg - firstAvg) / firstAvg) * 100).toFixed(1) : "0.0"

  const fmt = val =>
    isPercent
      ? `${(Number(val) * 100).toFixed(0)}%`
      : `£${Math.round(Number(val)).toLocaleString("en-GB")}`

  const fmtShort = val =>
    isPercent
      ? `${(Number(val) * 100).toFixed(0)}%`
      : `£${(Number(val) / 1000).toFixed(0)}k`

  const options = {
    chart: {
      type: "area",
      toolbar: { show: false },
      background: "transparent",
      foreColor: "#1a1a2e",
      animations: { enabled: true, easing: "easeinout", speed: 800 },
    },
    colors: [color],
    stroke: { show: true, curve: "smooth", width: 2.5 },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.35,
        opacityTo: 0.02,
        stops: [0, 90, 100],
        colorStops: [
          { offset: 0, color: gradientColor, opacity: 0.35 },
          { offset: 100, color: gradientColor, opacity: 0.02 },
        ],
      },
    },
    markers: {
      size: 4,
      colors: ["#fff"],
      strokeColors: color,
      strokeWidth: 2,
      hover: { size: 6 },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        // ✅ No rotation needed for W-1, W-2 short labels
        rotate: useWeeklyLabels ? 0 : -45,
        style: { colors: "#94a3b8", fontSize: "11px", fontWeight: 600 },
      },
    },
    yaxis: {
      labels: {
        style: { colors: "#94a3b8", fontSize: "11px", fontWeight: 600 },
        formatter: val => fmtShort(val),
      },
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    tooltip: {
      theme: "light",
      shared: true,
      intersect: false,
      // ✅ Show actual date on hover in weekly mode
      x: {
        formatter: (val, { dataPointIndex }) => {
          return getWeekTooltipLabel(
            dataPointIndex,
            actualDates[dataPointIndex]
          )
        },
      },
      y: { formatter: val => fmt(val) },
    },
    legend: { show: false },
  }

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "16px",
        padding: "24px",
        border: "1px solid #f1f5f9",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      }}
    >
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <span style={{ fontSize: "18px" }}>{icon}</span>
            <h6
              className="mb-0 fw-bold"
              style={{ color: "#1a1a2e", fontSize: "15px" }}
            >
              {title}
            </h6>
          </div>
          <small style={{ color: "#94a3b8", fontSize: "12px" }}>
            {sorted.length > 0 ? (
              <>
                {moment(sorted[0].date).format("MMM YYYY")} —{" "}
                {moment(sorted[sorted.length - 1].date).format("MMM YYYY")}{" "}
                <span style={{ color: "#666", fontWeight: 500 }}>(Year To Date)</span>
              </>
            ) : (
              "No data"
            )}
          </small>
        </div>

        {/* Trend badge */}
        <div
          style={{
            background: trendUp ? "#f0fdf4" : "#fef2f2",
            color: trendUp ? "#16a34a" : "#dc2626",
            borderRadius: "20px",
            padding: "4px 10px",
            fontSize: "12px",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <span>{trendUp ? "▲" : "▼"}</span>
          <span>{Math.abs(trendPct)}%</span>
        </div>
      </div>

      {/* Total value */}
      <div className="mb-3">
        <div
          style={{
            fontSize: "28px",
            fontWeight: 800,
            color: "#1a1a2e",
            lineHeight: 1,
          }}
        >
          {fmt(total)}
        </div>
        <small style={{ color: "#94a3b8", fontSize: "12px" }}>
          Total {title}
        </small>
      </div>

      {/* Chart */}
      <ReactApexChart
        options={options}
        series={[{ name: title, data: values }]}
        type="area"
        height={200}
        width="100%"
      />

      {/* Stats row */}
      <div
        className="d-flex justify-content-between pt-3"
        style={{ borderTop: "1px solid #f1f5f9" }}
      >
        {[
          { label: "Peak", value: fmt(max), color: "#22c55e" },
          { label: "Lowest", value: fmt(min), color: "#ef4444" },
          { label: "Average", value: fmt(avg), color },
        ].map((s, i) => (
          <div key={i} style={{ textAlign: "center" }}>
            <div style={{ fontSize: "14px", fontWeight: 800, color: s.color }}>
              {s.value}
            </div>
            <div
              style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600 }}
            >
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Main Dashboard ──────────────────────────────────────────────────────────
const MarginDashboard = ({ data }) => {
  const sausageData = data || []

  return (
    <div
      style={{ padding: "24px", background: "#f8fafc" }}
      className="rounded-4"
    >
      <div className="row g-4">
        <div className="col-md-6">
          <MarginLineChart
            data={sausageData?.sausageData}
            title="Gross Margin (All Categories)"
            color="#4361ee"
            gradientColor="#4361ee"
            icon="💰"
            valueKey="totalGrossMarginValue"
          />
        </div>
        <div className="col-md-6">
          <MarginLineChart
            data={sausageData?.finalNetMargin}
            title="Net Margin (All Categories)"
            color="#22c55e"
            gradientColor="#22c55e"
            icon="📊"
            valueKey="netMarginFinal"
          />
        </div>
      </div>
    </div>
  )
}

export default MarginDashboard
